/**
 * Workspace-file access that works in both pod topologies: direct fs
 * normally, over the launcher in split-mode pods (the repo lives only in the
 * workbench container). Only conveyor-agent's OWN reads route through here —
 * Claude Code's file tools run inside the workbench and stay native.
 */

import {
  readFile as localReadFile,
  readdir as localReaddir,
  stat as localStat,
} from "node:fs/promises";
import { getWorkbenchClient } from "./client.js";
import { workbenchEnabled } from "./mode.js";

export async function readWorkspaceFile(path: string): Promise<string> {
  if (workbenchEnabled()) {
    return (await getWorkbenchClient().readFile(path)).toString("utf8");
  }
  return localReadFile(path, "utf-8");
}

/** Binary-safe read — returns the raw bytes (never utf8-decoded). Used for
 *  attachment uploads, where the file may be a png/pdf/zip that must survive
 *  round-tripping. Routes through the workbench in split-mode pods, so a file
 *  Claude Code wrote into the workbench container is visible to the agent
 *  container's upload path. */
export function readWorkspaceBytes(path: string): Promise<Buffer> {
  if (workbenchEnabled()) return getWorkbenchClient().readFile(path);
  return localReadFile(path);
}

export function readWorkspaceDir(path: string): Promise<string[]> {
  if (workbenchEnabled()) return getWorkbenchClient().readdir(path);
  return localReaddir(path);
}

export interface WorkspacePathInfo {
  exists: boolean;
  isFile: boolean;
  isDirectory: boolean;
  size: number;
  mtimeMs: number;
}

export async function statWorkspacePath(path: string): Promise<WorkspacePathInfo> {
  if (workbenchEnabled()) return getWorkbenchClient().stat(path);
  try {
    const s = await localStat(path);
    return {
      exists: true,
      isFile: s.isFile(),
      isDirectory: s.isDirectory(),
      size: s.size,
      mtimeMs: s.mtimeMs,
    };
  } catch {
    return { exists: false, isFile: false, isDirectory: false, size: 0, mtimeMs: 0 };
  }
}

export async function workspacePathExists(path: string): Promise<boolean> {
  return (await statWorkspacePath(path)).exists;
}
